import React, { useEffect, useRef, useState } from 'react';
import styled from 'astroturf/react';
import { useIntersectionObserver, useResizeObserver } from 'usehooks-ts';

import { BuildVuPage } from './api';
import _ScrollContainer from "Page-ScrollContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/liswp/build_vu/Page.tsx?styleId=ScrollContainer";
import _ResizeContainer from "Page-ResizeContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/liswp/build_vu/Page.tsx?styleId=ResizeContainer";


const ScrollContainer = /*#__PURE__*/styled("div", null, {
  displayName: "ScrollContainer",
  styles: _ScrollContainer
});

const ResizeContainer = /*#__PURE__*/styled("div", null, {
  displayName: "ResizeContainer",
  styles: _ResizeContainer
});

export type PageProps = {
  page: BuildVuPage;
  load: boolean;
  onIntersect: (pageNumber: number) => void;
};

export const Page = React.memo(
  ({ page, load, onIntersect }: PageProps) => {
    const [pageFetched, setPageFetched] = useState(false);
    const [pageWidth, setPageWidth] = useState(page.width);
    const [pageHeight, setPageHeight] = useState(page.height);
    const shadowHostRef = useRef<HTMLDivElement>(null);

    // Let parent know when this page scrolls into view
    const { ref: intersectionRef } = useIntersectionObserver({
      freezeOnceVisible: true,
      onChange: (isIntersecting) => {
        if (isIntersecting) {
          onIntersect(page.page_number);
        }
      },
    });

    const resizeRef = useRef<HTMLDivElement>(null);

    const { width } = useResizeObserver({ ref: resizeRef });

    const setupShadowDOM = React.useCallback(
      (html: string) => {
        if (!shadowHostRef.current) return;

        if (!shadowHostRef.current.shadowRoot) {
          const shadowRoot = shadowHostRef.current.attachShadow({
            mode: 'open',
          });
          const container = document.createElement('div');
          container.style.width = '100%';
          container.style.height = '100%';
          shadowRoot.appendChild(container);
        }

        const shadowRoot = shadowHostRef.current.shadowRoot;
        if (shadowRoot) {
          const container = shadowRoot.firstElementChild as HTMLDivElement;
          if (container) {
            container.innerHTML = html;

            // Move font styles to the document head since shadow DOM doesn't support them
            const fontStyle = container.querySelector(
              `style#fonts${page.page_number}`,
            );
            if (fontStyle) {
              const newStyle = document.createElement('style');
              newStyle.id = `fonts1-page-${page.page_number}`;
              newStyle.textContent = fontStyle.textContent;
              document.head.appendChild(newStyle);
              fontStyle.remove();
            }
          }
        }
      },
      [page.page_number],
    );

    useEffect(() => {
      if (pageFetched || !load) {
        return;
      }

      const getPageHtml = async () => {
        const response = await fetch(page.page_url);

        if (response.ok) {
          const html = await response.text();

          // Extract dimensions from HTML when preloading, because we probably don't
          // have the manifest yet with the correct dimensions. Without this, the page
          // will not just be the wrong aspect ratio, it will overflow.
          if (page.preload) {
            const match = html.match(
              /id="jpedal"[^>]*width:\s*(\d+)px[^>]*height:\s*(\d+)px/,
            );
            if (match) {
              setPageWidth(parseInt(match[1], 10));
              setPageHeight(parseInt(match[2], 10));
            }
          }

          setupShadowDOM(html);
        }
      };
      getPageHtml();

      setPageFetched(true);
    }, [load, page, pageFetched, setupShadowDOM]);

    return (
      <ScrollContainer
        ref={intersectionRef}
        style={{ aspectRatio: `${pageWidth}/${pageHeight}` }}
      >
        <ResizeContainer ref={resizeRef}>
          <div
            ref={shadowHostRef}
            style={{
              transform: `scale(${width ? width / pageWidth : 1})`,
              transformOrigin: 'left top',
              width: '100%',
              height: '100%',
            }}
          />
        </ResizeContainer>
      </ScrollContainer>
    );
  },
  (prevProps, nextProps) =>
    // Explicitly ignore onIntersect changing (it always changes)
    prevProps.page === nextProps.page && prevProps.load === nextProps.load,
);
